/**
 * Edits of a table's row list, as the inspector makes them. Every function
 * is pure and returns a new list; the input list is never changed. A string
 * in place of a list is the reason an edit was refused, worded for the
 * researcher.
 */
import { nameKey, refusedName } from "../../compiler/names"
import type { ResolvedTable } from "../../model/resolve"
import { isNps, labelsInRange } from "../../model/scale"
import type { CatalogueQuestion } from "../../types/catalogue"
import type { RowItem } from "../../types/tabSpecDocument"

/**
 * The name the finished table shows for a row: an option's rename, or its
 * label as shown (`shownOf`, the label itself by default); a net's or a
 * difference's name.
 */
const shownName = (r: RowItem, shownOf: (label: string) => string = (l) => l): string | null =>
  r.kind === "option" ? r.rename?.trim() || shownOf(r.label) : r.kind === "mean" ? null : r.name

/**
 * Every name a row goes by: the one it shows and, for an option, its label
 * too, which is its name in the engine.
 */
const namesOf = (r: RowItem): string[] => {
  const shown = shownName(r)
  if (shown === null) return []
  return r.kind === "option" && shown !== r.label ? [r.label, shown] : [shown]
}

const sameName = (a: string, b: string): boolean => nameKey(a) === nameKey(b)

/** Whether any row goes by `name`, in any case. */
const hasName = (rows: RowItem[], name: string): boolean =>
  rows.some((r) => namesOf(r).some((own) => sameName(own, name)))

/** The rows as shown, to start editing from: the defaults are already filled in. */
export const materialise = (resolved: ResolvedTable): RowItem[] =>
  resolved.rows.map((r) => (r.kind === "net" ? { ...r, members: [...r.members] } : { ...r }))

/**
 * Moves the row at `from` to `to`; both are clamped to the list. A difference
 * may end up above one of its nets: the compiler reports that.
 */
export const moveRow = (rows: RowItem[], from: number, to: number): RowItem[] => {
  if (rows.length === 0) return []
  const clamp = (i: number): number => Math.max(0, Math.min(rows.length - 1, i))
  const next = [...rows]
  const [moved] = next.splice(clamp(from), 1)
  next.splice(clamp(to), 0, moved)
  return next
}

/** Hides or shows an answer option; showing it removes the field. */
export const setHidden = (rows: RowItem[], label: string, hidden: boolean): RowItem[] =>
  rows.map((r) => {
    if (r.kind !== "option" || r.label !== label) return r
    if (hidden) return { ...r, hidden: true }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { hidden: _gone, ...rest } = r
    return rest
  })

/** Keeps an answer option at the end when the rows are sorted, or lets it move again; letting it go removes the field. */
export const setAnchor = (rows: RowItem[], label: string, anchor: boolean): RowItem[] =>
  rows.map((r) => {
    if (r.kind !== "option" || r.label !== label) return r
    if (anchor) return { ...r, anchor: true }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { anchor: _gone, ...rest } = r
    return rest
  })

/**
 * Renames an answer option in the table. An empty name, the option's own
 * label, or the name it is shown under without a rename (`shownOf`, its
 * shown label; the label itself by default) removes the rename. The name the
 * row would show after the edit (the rename kept, or the shown label once
 * the rename is removed) goes through the compiler's rule for shown names
 * (`refusedName`), against the names the other rows show, their shown
 * labels included: two rows showing one name would share an id in the
 * finished table. A shown label is only shown, so the engine's character
 * rule does not apply to it.
 */
export const renameRow = (
  rows: RowItem[],
  label: string,
  rename: string,
  shownOf: (label: string) => string = (l) => l
): RowItem[] | string => {
  const text = rename.trim()
  const plain = shownOf(label)
  const keep = text !== "" && text !== label && text !== plain
  const shown = keep ? text : plain
  const others = rows.flatMap((r) => {
    if (r.kind === "option" && r.label === label) return []
    const other = shownName(r, shownOf)
    return other === null ? [] : [other]
  })
  const refused = refusedName(shown, others, "row", keep || plain === label ? "engine" : "display")
  if (refused) return refused
  return rows.map((r) => {
    if (r.kind !== "option" || r.label !== label) return r
    if (keep) return { ...r, rename: text }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { rename: _gone, ...rest } = r
    return rest
  })
}

/**
 * Adds a net of `members` at `at`, or right after the last answer option.
 * Refused, with the reason, for a name the compiler's rule for shown names
 * (`refusedName`) refuses against every name another row goes by (an
 * option's label or rename, a net's or a difference's name); and for a net
 * with no members.
 */
export const addNet = (
  rows: RowItem[],
  name: string,
  members: string[],
  at?: number
): RowItem[] | string => {
  const text = name.trim()
  const refused = refusedName(text, rows.flatMap(namesOf))
  if (refused) return refused
  if (members.length === 0) return "Choose at least one option."
  const net: RowItem = { kind: "net", name: text, members: [...members] }
  const lastOption = rows.reduce((last, r, i) => (r.kind === "option" ? i : last), -1)
  const index = at === undefined ? lastOption + 1 : Math.max(0, Math.min(rows.length, at))
  return [...rows.slice(0, index), net, ...rows.slice(index)]
}

/** A difference with a side not chosen, and one of a row with itself. */
export const DIFFERENCE_NEEDS_ROWS = "Choose the two rows."
export const DIFFERENCE_NEEDS_TWO = "Choose two different rows."

/**
 * Adds a difference row, `plus` minus `minus` (their column percentages, in
 * points), at the end, above the mean when there is one, so both its rows are
 * above it. Each side is a net by its name or an answer option by its label.
 * Refused, with the reason, for a name the compiler's rule for shown names
 * (`refusedName`) refuses against every name another row goes by, for a side
 * not chosen, and for the same row on both sides.
 */
export const addDifference = (
  rows: RowItem[],
  name: string,
  plus: string,
  minus: string
): RowItem[] | string => {
  const text = name.trim()
  const refused = refusedName(text, rows.flatMap(namesOf))
  if (refused) return refused
  if (!plus || !minus) return DIFFERENCE_NEEDS_ROWS
  if (plus === minus) return DIFFERENCE_NEEDS_TWO
  const difference: RowItem = { kind: "difference", name: text, plus, minus }
  const mean = rows.findIndex((r) => r.kind === "mean")
  const at = mean < 0 ? rows.length : mean
  return [...rows.slice(0, at), difference, ...rows.slice(at)]
}

/**
 * Removes the net, difference or mean at `index`. Answer options stay (hide
 * them instead). A net takes every difference that names it with it.
 */
export const removeItem = (rows: RowItem[], index: number): RowItem[] => {
  const item = rows[index]
  if (!item || item.kind === "option") return [...rows]
  if (item.kind !== "net") return rows.filter((_, i) => i !== index)
  const name = item.name.trim()
  return rows.filter(
    (r, i) =>
      i !== index &&
      !(r.kind === "difference" && (r.plus.trim() === name || r.minus.trim() === name))
  )
}

/** Adds the mean at the end, unless the table has one. */
export const addMean = (rows: RowItem[]): RowItem[] =>
  rows.some((r) => r.kind === "mean") ? [...rows] : [...rows, { kind: "mean" }]

/**
 * Adds Promoters (9 and 10), Passives (7 and 8), Detractors (0 to 6) and NPS
 * (Promoters minus Detractors), each unless a row of that name exists. They
 * go at the end, above the mean when there is one. Refused unless the
 * scores are exactly 0 to 10.
 */
export const addNps = (
  rows: RowItem[],
  q: CatalogueQuestion,
  scores: Record<string, number>
): RowItem[] | string => {
  if (!isNps(scores)) return "NPS needs a 0 to 10 scale."
  const wanted: RowItem[] = [
    { kind: "net", name: "Promoters", members: labelsInRange(q, scores, 9, 10) },
    { kind: "net", name: "Passives", members: labelsInRange(q, scores, 7, 8) },
    { kind: "net", name: "Detractors", members: labelsInRange(q, scores, 0, 6) },
    { kind: "difference", name: "NPS", plus: "Promoters", minus: "Detractors" },
  ]
  const added = wanted.filter((w) => !hasName(rows, shownName(w) ?? ""))
  const mean = rows.findIndex((r) => r.kind === "mean")
  const at = mean < 0 ? rows.length : mean
  return [...rows.slice(0, at), ...added, ...rows.slice(at)]
}
